import {
  clearSessionStorageValues,
  IAutoPolicy,
  IAutoQuote,
  IAutoQuoteApplication,
  IAutoCheckout,
  IAutoEnrollmentApplication,
  IAutoQuoteEnrollment,
  IUser,
  IRentersPolicy,
  IRentersBreakUp,
  IRentersCheckout,
  IRentersCreateQuote,
  IOktaLoginInfo,
  IRentersQuote,
  IRentersQuoteApplication,
  IRentersQuoteDetails,
  changePassword,
  IAutoVariablesPayload,
  IRentersVariablesPayload,
  TPolicy,
  TPolicyHealthCheckErrorState,
  IRentersQuotePlan,
  IRentersVariables,
  getIsCypressOnLocalhost,
  IOktaIdToken,
  IOktaAccessToken,
} from '@sureapp/platform-sdk';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import moment from 'moment';

import {
  IFeatureFlag,
  IQuoteData,
  IQuoteHealthCheckState,
} from '@@/interfaces';
import getFormattedCoverageVars from '@@/services/getFormattedCoverageVars';
import { isAutoPolicyVars, isRentersQuote } from '@@/services/typeGuards';
import { generateUUID } from '@@/utils';

import api from '../api';

import * as createAction from './actionCreators';
import {
  sanitizeAddress,
  sanitizeClient,
  sanitizeHolders,
  clearSDKState,
} from './helpers';
import { AppDispatch, store } from './index';

const OKTA_ID_TOKEN_KEY = 'TOGGLE_ID_TOKEN_KEY';
const OKTA_ACCESS_TOKEN_KEY = 'TOGGLE_ACCESS_TOKEN_KEY';

/**
 * XXX: Setting up for Auto store
 * Enrollment Application (Auto)
 * =========================
 */

export function updateAutoEnrollmentApplication(
  application: IAutoEnrollmentApplication
) {
  return (dispatch: AppDispatch) => {
    return new Promise<void>((resolve) => {
      dispatch(createAction.updateEnrollmentApplication(application));
      resolve();
    });
  };
}

export function clearEnrollmentApplication() {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.clearEnrollmentApplication());
  };
}

/**
 * XXX: Setting up for Auto store
 * Clear Quote (Auto)
 * =========================
 */

export function clearQuote() {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.clearAutoQuote());
    clearSDKState();
    clearSessionStorageValues();
    return Promise.resolve();
  };
}

/**
 * XXX: Setting up for Auto store
 * Clear Quote (Auto)
 * =========================
 */

export function clearSDKStorage() {
  return () => {
    clearSDKState();
    return Promise.resolve();
  };
}

/**
 * XXX: Setting up for Auto store
 * Create Quote (Auto)
 * =========================
 */

export function createAutoQuote(enrollmentApplication: IAutoQuoteEnrollment) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.createAutoQuoteRequest(enrollmentApplication));
    return new Promise((resolve, reject) => {
      api
        .createQuote(enrollmentApplication)
        .then((quote) => {
          dispatch(createAction.createAutoQuoteSuccess(quote));
          resolve(quote);
        })
        .catch((error) => {
          dispatch(createAction.createAutoQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Get Quote (Auto)
 * =========================
 */

export function getAutoQuote(quoteNumber: string) {
  return (dispatch: AppDispatch): Promise<IAutoQuote> => {
    dispatch(createAction.getAutoQuoteRequest(quoteNumber));
    return new Promise((resolve, reject) => {
      api
        .getQuote<IAutoQuote>(quoteNumber)
        .then((quote) => {
          dispatch(createAction.getAutoQuoteSuccess(quote));
          resolve(quote);
        })
        .catch((error) => {
          dispatch(createAction.getAutoQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Update Quote (Auto)
 * =========================
 */

export function updateQuote<T>(
  quote_number: string,
  updatedQuote: IAutoQuote | IAutoQuoteEnrollment | IRentersQuote
) {
  return (dispatch: AppDispatch): Promise<T> => {
    dispatch(
      isRentersQuote(updatedQuote)
        ? createAction.updateRentersQuoteRequest({ quote: updatedQuote })
        : createAction.updateAutoQuoteRequest(updatedQuote)
    );
    return new Promise((resolve, reject) => {
      api
        .updateQuote(quote_number, updatedQuote)
        .then((quote) => {
          dispatch(
            isRentersQuote(quote)
              ? createAction.updateRentersQuoteSuccess({ quote })
              : createAction.updateAutoQuoteSuccess(quote)
          );
          resolve(quote);
        })
        .catch((error) => {
          dispatch(createAction.updateQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Get Quote Vars (Auto)
 * =========================
 */

export function getAutoQuoteCheckout(
  quoteNumber: string,
  application?: IAutoQuoteApplication
) {
  return (dispatch: AppDispatch): Promise<IAutoCheckout> => {
    dispatch(
      createAction.getAutoQuoteCheckoutRequest({ quoteNumber, application })
    );
    return new Promise((resolve, reject) => {
      api
        .checkoutQuote(quoteNumber, application)
        .then((quoteCheckout) => {
          dispatch(createAction.getAutoQuoteCheckoutSuccess(quoteCheckout));
          resolve(quoteCheckout);
        })
        .catch((error) => {
          dispatch(createAction.getAutoQuoteCheckoutFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Get Vehicle Data (Auto)
 * =========================
 */

export function getVehiclesYears(regionCode: string) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.getVehiclesYearsRequest(regionCode));
    return new Promise((resolve, reject) => {
      api
        .getVehiclesYears(regionCode)
        .then((years) => {
          dispatch(createAction.getVehiclesYearsSuccess(years));
          resolve(years);
        })
        .catch((error) => {
          dispatch(createAction.getVehiclesYearsFailure(error));
          reject(error);
        });
    });
  };
}

export function getVehiclesMakes(year: number, regionCode: string) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.getVehiclesMakesRequest({ year, regionCode }));
    return new Promise((resolve, reject) => {
      api
        .getVehiclesMakes(year, regionCode)
        .then((makes) => {
          dispatch(createAction.getVehiclesMakesSuccess(makes));
          resolve(makes);
        })
        .catch((error) => {
          dispatch(createAction.getVehiclesMakesFailure(error));
          reject(error);
        });
    });
  };
}

export function getVehiclesModels(
  year: number,
  make: string,
  regionCode: string
) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.getVehiclesModelsRequest({ year, make, regionCode }));
    return new Promise((resolve, reject) => {
      api
        .getVehiclesModels(year, make, regionCode)
        .then((models) => {
          dispatch(createAction.getVehiclesModelsSuccess(models));
          resolve(models);
        })
        .catch((error) => {
          dispatch(createAction.getVehiclesModelsFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Get Quote Checkout (Auto)
 * =========================
 */

export function getAutoQuoteVars(quoteNumber: string) {
  return (dispatch: AppDispatch): Promise<IAutoVariablesPayload> => {
    dispatch(createAction.getAutoQuoteVarsRequest(quoteNumber));
    return new Promise((resolve, reject) => {
      api
        .getQuoteVars(quoteNumber)
        .then((quoteVars) => {
          const formattedQuoteVars = getFormattedCoverageVars(quoteVars);
          dispatch(createAction.getAutoQuoteVarsSuccess(formattedQuoteVars));
          resolve(quoteVars);
        })
        .catch((error) => {
          dispatch(createAction.getAutoQuoteVarsFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Finalize Quote (Auto)
 * =========================
 */

export function finalizeAutoQuote(
  quoteNumber: string,
  opts: { username?: string; password?: string }
) {
  return (dispatch: AppDispatch): Promise<IAutoQuote> => {
    dispatch(createAction.finalizeAutoQuoteRequest({ quoteNumber, opts }));
    return new Promise((resolve, reject) => {
      api
        .finalizeQuote<IAutoQuote>(quoteNumber, opts)
        .then((finalizeInfo) => {
          dispatch(createAction.finalizeAutoQuoteSuccess(finalizeInfo));
          resolve(finalizeInfo);
        })
        .catch((error) => {
          dispatch(createAction.finalizeAutoQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * XXX: Setting up for Auto store
 * Update Cached Quote Health Check (Auto)
 * =========================
 */

export function updateCachedQuoteHealthCheckState(
  payload: IQuoteHealthCheckState
) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateCachedQuoteHealthCheckState(payload));
  };
}

/**
 * Update Policy Health Check Error Details (Auto)
 * =========================
 */

export function updatePolicyHealthCheckErrorDetails(
  payload: TPolicyHealthCheckErrorState
) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updatePolicyHealthCheckErrorDetails(payload));
  };
}

export function clearPolicyHealthCheckErrorDetails() {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.clearPolicyHealthCheckErrorDetails());
  };
}

/**
 * Update QuoteData store
 * =========================
 */

export function updateQuoteData(payload: Partial<IQuoteData>) {
  return (dispatch: AppDispatch) => {
    return new Promise<void>((resolve) => {
      dispatch(createAction.updateQuoteData(payload));
      resolve();
    });
  };
}

/**
 * Create quote
 * =========================
 */

export function createQuote(opts: IRentersCreateQuote) {
  return (dispatch: AppDispatch): Promise<IRentersQuote> => {
    const { urlParams } = store.getState();
    const client = {
      address: sanitizeAddress(opts.ratingAddress),
      first_name: opts.firstName,
      last_name: opts.lastName,
      dob: moment(opts.dob, 'MM/DD/YYYY').format('YYYY-MM-DD'),
    };
    const holder = {
      ...client,
      address: sanitizeAddress(opts.holderAddress || opts.ratingAddress),
    };
    const payload = {
      application: {
        client,
        holders: [
          {
            ...holder,
            position_on_policy: 1,
          },
        ],
        rating_address: sanitizeAddress(opts.ratingAddress),
        policy_effective_date: moment().add(1, 'day').format('YYYY-MM-DD'),
        metadata: {
          affiliate_code: urlParams.affiliate_code,
          offer_id: urlParams.offer_id,
          transaction_id: urlParams.transaction_id,
          utm_medium: urlParams.utm_medium,
          utm_source: urlParams.utm_source,
        },
        auto_renewal: true,
      },
      vertical_code: 'farmers_renters',
    };

    dispatch(createAction.createQuoteRequest(opts));
    return new Promise((resolve, reject) => {
      api
        .createQuote(payload)
        .then((quote) => {
          dispatch(createAction.createQuoteSuccess({ quote }));
          resolve(quote);
        })
        .catch((error) => {
          dispatch(createAction.createQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get plans
 * =========================
 */

export function getPlans(quoteId: string) {
  return (dispatch: AppDispatch): Promise<{ plans: IRentersQuotePlan[] }> => {
    dispatch(createAction.getPlansRequest(quoteId));
    return new Promise((resolve, reject) => {
      api
        .getPlans(quoteId)
        .then((plansResp) => {
          dispatch(createAction.getPlansSuccess(plansResp));
          resolve(plansResp);
        })
        .catch((error) => {
          dispatch(createAction.getPlansFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Update quote
 * =========================
 */

export function updateRentersQuote(updatedQuote?: IRentersQuote) {
  return (dispatch: AppDispatch): Promise<IRentersQuote> => {
    const { quote, quoteDetails, variableSelections } = store.getState();
    return new Promise((resolve, reject) => {
      if (quote) {
        const client = {
          ...quote?.application?.client,
          phone_number: quoteDetails.phone,
          email: quoteDetails.email,
        };
        const holders = sanitizeHolders([
          {
            ...client,
            position_on_policy: 1,
            phone_number: quoteDetails.phone,
            email: quoteDetails.email,
          },
        ]);

        if (
          quoteDetails.hasAdditionalHolder &&
          quoteDetails.additionalHolderFirstName &&
          quoteDetails.additionalHolderLastName
        ) {
          holders.push({
            first_name: quoteDetails.additionalHolderFirstName,
            last_name: quoteDetails.additionalHolderLastName,
            position_on_policy: 2,
          });
        }

        let billing = quote?.application?.billing;
        if (quoteDetails.stripeToken) {
          billing = {
            billing_cadence: 'monthly',
            billing_type: 'platform_eft',
            source_token: quoteDetails.stripeToken,
          };
        }

        const updatedApplication = {
          billing,
          client: sanitizeClient(client),
          holders,
          plan_id: quoteDetails.planId,
          variables: variableSelections,
          policy_effective_date: quoteDetails.startDate
            ? moment.utc(quoteDetails.startDate).format('YYYY-MM-DD')
            : moment().add(1, 'day').format('YYYY-MM-DD'),
        };
        const application = {
          ...(quote?.application as IRentersQuoteApplication),
          ...updatedApplication,
        };

        dispatch(
          createAction.updateQuoteRequest(
            (updatedQuote?.application as IRentersQuoteApplication) ||
              application
          )
        );
        api
          .updateQuote(quote.quote_number, updatedQuote || { application })
          .then((updatedQuoteRes) => {
            dispatch(
              createAction.updateQuoteSuccess({ quote: updatedQuoteRes })
            );
            resolve(updatedQuoteRes);
          })
          .catch((error) => {
            dispatch(createAction.updateRentersQuoteFailure(error));
            reject(error);
          });
      }
    });
  };
}

/**
 * Get quote vars
 * =========================
 */

export function getQuoteVars() {
  return (dispatch: AppDispatch): Promise<IRentersVariablesPayload> => {
    const { quote } = store.getState();

    dispatch(createAction.getQuoteVarsRequest());
    return new Promise((resolve, reject) => {
      api
        .getQuoteVars(quote?.quote_number)
        .then((quoteVars) => {
          dispatch(createAction.getQuoteVarsSuccess(quoteVars));
          resolve(quoteVars);
        })
        .catch((error) => {
          dispatch(createAction.getQuoteVarsFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Checkout quote
 * =========================
 */

export function checkoutQuote() {
  return (dispatch: AppDispatch) => {
    const { quote } = store.getState();

    if (isEmpty(quote)) {
      return Promise.reject(
        Object.assign(
          new Error(
            'There was a problem finding your quote. Please try refreshing and starting over.'
          ),
          {
            code: 'missing_quote',
          }
        )
      );
    }

    dispatch(createAction.checkoutQuoteRequest());
    return new Promise((resolve, reject) => {
      api
        .checkoutQuote(quote?.quote_number)
        .then((checkoutInfo) => {
          dispatch(createAction.checkoutQuoteSuccess(checkoutInfo));
          resolve(checkoutInfo);
        })
        .catch((error) => {
          dispatch(createAction.checkoutQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Update quote details
 * =========================
 */

export function updateQuoteDetails(details: Partial<IRentersQuoteDetails>) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateQuoteDetails(details));
  };
}

/**
 * Update variable selections
 * ==============================
 */

export function updateVariableSelections(
  variables: Partial<IRentersVariables>
) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateVariableSelections(variables));
  };
}

/**
 * Finalize quote
 * =========================
 */

export function finalizeQuote(opts: { [key: string]: string } = {}) {
  return (dispatch: AppDispatch): Promise<IRentersQuote> => {
    const { quote, uuid } = store.getState();

    let uniqueId = uuid?.uniqueId;
    if (!uniqueId) {
      uniqueId = generateUUID();
    }

    dispatch(
      createAction.finalizeQuoteRequest({ uuid: { ...opts, uniqueId } })
    );
    return new Promise((resolve, reject) => {
      api
        .finalizeQuote<IRentersQuote>(
          quote?.quote_number,
          { ...opts },
          uniqueId
        )
        .then((finalizeInfo) => {
          dispatch(createAction.finalizeQuoteSuccess({ quote: finalizeInfo }));
          resolve(finalizeInfo);
        })
        .catch((error) => {
          dispatch(createAction.finalizeQuoteFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get policies
 * =========================
 */

export function getPolicies() {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.getPoliciesRequest());
    return new Promise((resolve, reject) => {
      api
        .getPolicies()
        .then((policies) => {
          dispatch(createAction.getPoliciesSuccess({ policies }));
          resolve(policies);
        })
        .catch((error) => {
          dispatch(createAction.getPoliciesFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get policy
 * =========================
 */

export function getPolicy<Policy extends TPolicy = TPolicy>(policyId: string) {
  return (dispatch: AppDispatch): Promise<Policy> => {
    dispatch(createAction.getPolicyRequest());
    return new Promise((resolve, reject) => {
      api
        .getPolicy<Policy>(policyId)
        .then((policy) => {
          dispatch(
            createAction.getPolicySuccess<Policy>({ policy })
          );
          resolve(policy);
        })
        .catch((error) => {
          dispatch(createAction.getPolicyFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Update policy
 * =========================
 */

const activeDisputeStatuses = [
  'warning_needs_response',
  'warning_under_review',
  'evidence_submitted',
  'needs_response',
  'under_review',
];

function getHasOpenOrLostBillingDispute(policy: TPolicy) {
  return policy.bills.some((bill) =>
    (bill.details.disputes || []).some(({ status }) =>
      [...activeDisputeStatuses, 'lost'].includes(status)
    )
  );
}

export function updatePolicy(policyId: string, policyUpdates: TPolicy) {
  return (dispatch: AppDispatch): Promise<TPolicy> => {
    const { policies } = store.getState();
    const policy: TPolicy = find(policies, { id: policyId })!;
    const updatedPolicy = {
      ...policy,
      ...policyUpdates,
    };

    const hasOpenOrLostBillingDispute = getHasOpenOrLostBillingDispute(policy);

    return new Promise((resolve, reject) => {
      if (hasOpenOrLostBillingDispute) {
        reject(Object.assign(new Error(), { code: 'policy_under_dispute' }));
      } else {
        dispatch(createAction.updatePolicyRequest({ policy: updatedPolicy }));

        api
          .updatePolicy(updatedPolicy)
          .then((updatedPolicyRes) => {
            dispatch(
              createAction.updatePolicySuccess({ policy: updatedPolicyRes })
            );
            resolve(updatedPolicyRes);
          })
          .catch((error) => {
            dispatch(createAction.updatePolicyFailure(error));
            reject(error);
          });
      }
    });
  };
}

/**
 * Get policy vars
 * =========================
 */

export function getPolicyVars(policyId: string) {
  return (
    dispatch: AppDispatch
  ): Promise<IRentersVariablesPayload | IAutoVariablesPayload> => {
    dispatch(createAction.getPolicyVarsRequest(policyId));
    return new Promise((resolve, reject) => {
      api
        .getPolicyVars(policyId)
        .then((policyVars) => {
          const vars = isAutoPolicyVars(policyVars)
            ? getFormattedCoverageVars(policyVars)
            : policyVars;
          dispatch(createAction.getPolicyVarsSuccess(vars));
          resolve(policyVars);
        })
        .catch((error) => {
          dispatch(createAction.getPolicyVarsFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get policy checkout
 * =========================
 */

export function getPolicyCheckout(
  policy: TPolicy,
  options: { skip_fetch_additional_data: boolean } = {
    skip_fetch_additional_data: true,
  }
) {
  return (dispatch: AppDispatch): Promise<IAutoCheckout | IRentersCheckout> => {
    dispatch(createAction.getPolicyCheckoutRequest({ policyId: policy.id }));
    return new Promise((resolve, reject) => {
      api
        .getPolicyCheckout(policy, options)
        .then((policyCheckout) => {
          dispatch(createAction.getPolicyCheckoutSuccess(policyCheckout));
          resolve(policyCheckout);
        })
        .catch((error) => {
          dispatch(createAction.getPolicyCheckoutFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Editing policy
 * =========================
 */

export function updateEditingPolicy(policy: IRentersPolicy) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateEditingPolicy({ policy }));
  };
}

/**
 * Editing policy
 * =========================
 */

export function updateStagedAutoPolicy(policy: IAutoPolicy | {}) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateStagedAutoPolicyRequest({ policy }));
  };
}

export function clearStagedAutoPolicy() {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.clearStagedAutoPolicyRequest());
    window?.sessionStorage?.setItem?.('toggleAutoSignedDocs', '');
  };
}
/**
 * Cancel policy
 * =========================
 */

export function cancelPolicy(
  policyId: string,
  reason: string,
  comments?: string
) {
  return (dispatch: AppDispatch): Promise<TPolicy> => {
    dispatch(createAction.cancelPolicyRequest({ reason }));
    return new Promise((resolve, reject) => {
      api
        .cancelPolicy(policyId, reason, comments)
        .then((policy) => {
          dispatch(createAction.cancelPolicySuccess({ policy }));
          resolve(policy);
        })
        .catch((error) => {
          dispatch(createAction.cancelPolicyFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Save okta tokens
 * XXX: update types when adding okta types
 * =========================
 */

export function saveOktaIdToken(token: IOktaIdToken, auth: any) {
  return (dispatch: AppDispatch) => {
    const { hasLoggedInDuringPurchaseFlow } = store.getState();

    return new Promise((resolve) => {
      auth._oktaAuth.tokenManager.add(OKTA_ID_TOKEN_KEY, token);
      dispatch(
        createAction.saveOktaIdToken({ token, hasLoggedInDuringPurchaseFlow })
      );
      resolve(auth);
    });
  };
}

export function saveOktaAccessToken(token: IOktaAccessToken, auth: any) {
  return (dispatch: AppDispatch) => {
    return new Promise((resolve) => {
      api.setAuthHeader(token.accessToken);
      auth._oktaAuth.tokenManager.add(OKTA_ACCESS_TOKEN_KEY, token);
      dispatch(createAction.saveOktaAccessToken(token));
      resolve(auth);
    });
  };
}

/**
 * Refresh okta tokens
 * XXX: update types when adding okta types
 * =========================
 */

export function refreshOktaTokens(auth: any) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.refreshOktaTokensRequest());
    return Promise.all([
      auth._oktaAuth.tokenManager.renew(OKTA_ID_TOKEN_KEY),
      auth._oktaAuth.tokenManager.renew(OKTA_ACCESS_TOKEN_KEY),
    ])
      .then(([renewedIdToken, renewedAccessToken]) => {
        window.analytics.identify(renewedIdToken?.claims?.sub);
        api.setAuthHeader(renewedAccessToken.accessToken);
        dispatch(
          createAction.refreshOktaTokensSuccess([
            renewedIdToken,
            renewedAccessToken,
          ])
        );
        return [renewedIdToken, renewedAccessToken];
      })
      .catch((error) => {
        if (getIsCypressOnLocalhost()) {
          return { isCypress: true };
        }
        dispatch(createAction.refreshOktaTokensFailure(error));
        return error;
      });
  };
}

/**
 * Get the user
 * =========================
 */

export function getUser() {
  return (dispatch: AppDispatch): Promise<IUser> => {
    dispatch(createAction.getUserRequest());
    return new Promise((resolve, reject) => {
      api
        .getUser()
        .then((user) => {
          window.analytics.identify({
            first_name: user.client.person.first_name,
            last_name: user.client.person.last_name,
            email: user.client.person.email,
            phone_number: user.client.person.phone_number,
            address: omit(user.client.person.address, 'id'),
          });
          dispatch(createAction.getUserSuccess(user));
          resolve(user);
        })
        .catch((error) => {
          dispatch(createAction.getUserFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get feature flags
 * =========================
 */

export function getFeatureFlags() {
  return async (dispatch: AppDispatch): Promise<IFeatureFlag[]> => {
    dispatch(createAction.getFeatureFlagsRequest());

    try {
      const featureFlags = await api.getFeatureFlags();
      dispatch(createAction.getFeatureFlagsSuccess(featureFlags));
      return featureFlags;
    } catch (error) {
      dispatch(createAction.getFeatureFlagsFailure(error));
      throw error;
    }
  };
}

/**
 * Update the user
 * =========================
 */

export function updateUser(user: IUser) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateUserRequest(user));
    return new Promise((resolve, reject) => {
      api
        .updateUser(user)
        .then((updatedUser) => {
          dispatch(createAction.updateUserSuccess(updatedUser));
          resolve(updatedUser);
        })
        .catch((error) => {
          dispatch(createAction.updateUserFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Change user password
 * =========================
 */

export function updateUserPassword(newPassword: string, oldPassword: string) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.updateUserPasswordRequest());
    return new Promise((resolve, reject) => {
      changePassword(newPassword, oldPassword)
        .then((data) => {
          dispatch(createAction.updateUserPasswordSuccess());
          resolve(data);
        })
        .catch((error) => {
          dispatch(createAction.updateUserPasswordFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Create LevelCredit user
 * =========================
 */

export function createRenttrackUser() {
  return (dispatch: AppDispatch): Promise<void> => {
    dispatch(createAction.createRenttrackUserRequest());
    return new Promise((resolve, reject) => {
      api
        .createRenttrackUser()
        .then((data) => {
          dispatch(createAction.createRenttrackUserSuccess());
          resolve(data);
        })
        .catch((error) => {
          dispatch(createAction.createRenttrackUserFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Send prior coverage termination
 * (Break up letter)
 * ===================================
 */

export function sendPriorCoverageTermination(
  opts: Partial<IRentersBreakUp> = {}
) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.sendPriorCoverageTerminationRequest(opts));
    return new Promise((resolve, reject) => {
      const payload = {
        ...opts,
        cancellationDate: moment(opts.cancellationDate, 'MM/DD/YYYY').format(
          'YYYY-MM-DD'
        ),
      };
      api
        .sendPriorCoverageTermination(payload)
        .then((data) => {
          dispatch(createAction.sendPriorCoverageTerminationSuccess());
          resolve(data);
        })
        .catch((error) => {
          dispatch(createAction.sendPriorCoverageTerminationFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Okta login info after finalizing quote
 * =========================
 */

export function createOktaLoginInfo(loginInfo: IOktaLoginInfo) {
  return (dispatch: AppDispatch) =>
    new Promise<void>((resolve) => {
      dispatch(createAction.createOktaLoginInfo(loginInfo));
      resolve();
    });
}

export function clearOktaLoginInfo() {
  return (dispatch: AppDispatch) =>
    new Promise<void>((resolve) => {
      dispatch(createAction.clearOktaLoginInfo);
      resolve();
    });
}

/**
 * Exchange activation token
 * =========================
 */

export function exchangeActivationToken(token: string | null) {
  return (dispatch: AppDispatch) => {
    dispatch(createAction.exchangeActivationTokenRequest({ token }));
    return new Promise((resolve, reject) => {
      api
        .exchangeActivationToken(token)
        .then((resp) => {
          dispatch(createAction.exchangeActivationTokenSuccess(resp));
          resolve(resp);
        })
        .catch((error) => {
          dispatch(createAction.exchangeActivationTokenFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Logout
 * XXX: update types when adding okta types
 * =========================
 */

export function logout(auth: any) {
  return (dispatch: AppDispatch) => {
    return new Promise((resolve) => {
      auth._oktaAuth.tokenManager.remove(OKTA_ID_TOKEN_KEY);
      auth._oktaAuth.tokenManager.remove(OKTA_ACCESS_TOKEN_KEY);
      api.deleteAuthHeader();
      window.analytics.reset();
      dispatch(createAction.logout());
      resolve(true);
    });
  };
}

/**
 * Check rating address eligibility
 * =========================
 */

export function checkRatingAddressEligibility(policyId: string) {
  return (
    dispatch: AppDispatch
  ): Promise<{ is_eligible: boolean; ineligibility_message?: string }> => {
    dispatch(createAction.checkRatingAddressEligibilityRequest({ policyId }));
    return new Promise((resolve, reject) => {
      api
        .checkRatingAddressEligibility(policyId)
        .then((resp) => {
          dispatch(createAction.checkRatingAddressEligibilitySuccess(resp));
          resolve(resp);
        })
        .catch((error) => {
          dispatch(createAction.checkRatingAddressEligibilityFailure(error));
          reject(error);
        });
    });
  };
}

/**
 * Get cms content
 * @param {Array.<string>} slugs - An array of collection slugs
 */
export function getCmsContent(slugs: string[]) {
  return (dispatch: AppDispatch) => {
    return new Promise((resolve, reject) => {
      const apiPromises = slugs.map((slug) => {
        dispatch(createAction.getCmsContentRequest());
        return new Promise((resolveApiPromise, rejectApiPromise) => {
          api
            .getCmsContent(slug)
            .then((cmsContent) => {
              const data = {
                ...cmsContent,
                slug,
              };
              resolveApiPromise(data);
            })
            .catch((error) => {
              const data = {
                ...error,
                slug,
              };
              rejectApiPromise(data);
            });
        });
      });

      return Promise.allSettled(apiPromises).then((results) => {
        results.forEach((result) => {
          if (result.status === 'rejected') {
            dispatch(createAction.getCmsContentFailure(result));
            reject(result);
          } else {
            dispatch(createAction.getCmsContentSuccess(result.value));
            resolve(result.value);
          }
        });
      });
    });
  };
}

/**
 * User logged in during Purchace Flow
 * =========================
 */

export function setHasLoggedInDuringPurchaseFlow() {
  return (dispatch: AppDispatch) =>
    dispatch(createAction.updateHasLoggedInDuringPurchaseFlow(true));
}

/**
 * Clear Okta tokens for QA flows
 * =========================
 */
export function clearOktaForQA() {
  return (dispatch: AppDispatch) => {
    return new Promise((resolve) => {
      dispatch(createAction.clearOktaForQA(null));
      resolve(true);
    });
  };
}

/**
 * Exchange quote auth token
 * ===================================
 */

export function exchangeQuoteAuthToken(quoteNumber: string, token: string) {
  return (dispatch: AppDispatch) => {
    return new Promise<string>((resolve, reject) => {
      dispatch(
        createAction.exchangeQuoteAuthTokenRequest({ quoteNumber, token })
      );
      api
        .exchangeQuoteAuthToken(quoteNumber, token)
        .then(({ token: exchangedToken }) => {
          api.setAuthHeader(exchangedToken);
          dispatch(createAction.exchangeQuoteAuthTokenSuccess(exchangedToken));
          resolve(exchangedToken);
        })
        .catch((error) => {
          dispatch(createAction.exchangeQuoteAuthTokenFailure(error));
          reject(error);
        });
    });
  };
}
